/**
 * Inset cameras (03 §5, §10; 09 §12): extra top-down orthographic cameras with their own viewport,
 * ortho bounds and layer mask (default INSET 0x2), rendered after the main camera via
 * `scene.activeCameras`. Each clears its own viewport (scissored colour clear) before rendering.
 * Not user-controlled: styles drive them with `setView` / `setRect` (from `onLayout`).
 */
import { Color4 } from '@babylonjs/core/Maths/math.color';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { Engine } from '@babylonjs/core/Engines/engine';
import type { RGBA, Rect } from '../api.ts';
import { CameraSync, createTopDownCamera, type MapCameraHost, type TopDownCamera } from './camera.ts';
import { LAYER } from './engine.ts';
import { MapView } from './view.ts';

export interface InsetSpec {
  rect: Rect;
  center: [number, number];
  mpp: number;
  rotDeg: number;
  /** stage px where `center` lands; default: rect centre */
  centerPx?: [number, number];
  layerMask?: number;
  /** viewport clear colour (0..255 RGB, alpha 0..1); null = no clear (draw over the main map) */
  clear?: RGBA | null;
}

export interface InsetCamera {
  readonly cam: TopDownCamera;
  readonly view: MapView;
  setRect(r: Rect, centerPx?: [number, number]): void;
  setView(center: [number, number], mpp: number, rotDeg?: number): void;
  setClear(c: RGBA | null): void;
  worldToStage(x: number, y: number, out: Float32Array | number[]): void;
  stageToWorld(px: number, py: number, out: Float32Array | number[]): void;
  dispose(): void;
}

class SyncedView extends MapView {
  onChange: (() => void) | null = null;
  protected override changed(): void {
    this.onChange?.();
    super.changed();
  }
}

export class InsetCameraFactory {
  private readonly insets: InsetCamera[] = [];
  private readonly appliers = new Map<InsetCamera, () => void>();
  private n = 0;
  private readonly clears = new Map<Camera, Color4>();
  private obs: Observer<Camera> | null = null;

  constructor(
    private readonly host: MapCameraHost,
    private readonly engine: Engine,
    /** the main camera, kept first in activeCameras */
    private readonly main: Camera,
  ) {
    this.obs = host.scene.onBeforeCameraRenderObservable.add((cam) => {
      const c = this.clears.get(cam);
      if (!c) return;
      const vp = cam.viewport;
      const w = this.engine.getRenderWidth();
      const h = this.engine.getRenderHeight();
      const x = Math.floor(vp.x * w);
      const y = Math.floor(vp.y * h);
      const ww = Math.floor(vp.width * w);
      const hh = Math.floor(vp.height * h);
      this.engine.enableScissor(x, y, ww, hh);
      this.engine.clear(c, true, true, true);
      this.engine.disableScissor();
    });
  }

  get count(): number {
    return this.insets.length;
  }

  create(spec: InsetSpec): InsetCamera {
    const { scene, hub } = this.host;
    const cam = createTopDownCamera(`inset${String(this.n++)}`, scene, spec.layerMask ?? LAYER.INSET);
    const sync = new CameraSync(cam, hub);
    const view = new SyncedView(spec.rect);
    const host = this.host;
    const apply = (): void => {
      sync.apply(view, view.rect, host.pxScale, host.renderWidth(), host.renderHeight());
    };
    view.setRotation(spec.rotDeg);
    view.cx = spec.center[0];
    view.cy = spec.center[1];
    view.mpp = spec.mpp;
    const cp = spec.centerPx ?? [spec.rect.x + spec.rect.w / 2, spec.rect.y + spec.rect.h / 2];
    view.cpx = cp[0];
    view.cpy = cp[1];
    view.onChange = apply;
    apply();
    const clears = this.clears;
    const setClear = (c: RGBA | null): void => {
      if (c) clears.set(cam, new Color4(c[0] / 255, c[1] / 255, c[2] / 255, c[3]));
      else clears.delete(cam);
    };
    setClear(spec.clear === undefined ? [0, 0, 0, 1] : spec.clear);
    const ac = scene.activeCameras ?? [];
    scene.activeCameras = [this.main, ...ac.filter((c) => c !== this.main), cam];
    const inset: InsetCamera = {
      cam,
      view,
      setRect: (r, centerPx) => {
        // setRect + setCenterPx both bump the view version (tile layers recompute visibility)
        view.setRect(r);
        if (centerPx) view.setCenterPx(centerPx[0], centerPx[1]);
        else view.setCenterPx(r.x + r.w / 2, r.y + r.h / 2);
      },
      setView: (center, mpp, rotDeg) => {
        view.setViewRaw(center[0], center[1], mpp, rotDeg);
      },
      setClear,
      worldToStage: (x, y, out) => {
        view.worldToStage(x, y, out);
      },
      stageToWorld: (px, py, out) => {
        view.stageToWorld(px, py, out);
      },
      dispose: () => {
        clears.delete(cam);
        hub.removeCamera(cam);
        if (scene.activeCameras) scene.activeCameras = scene.activeCameras.filter((c) => c !== cam);
        cam.dispose();
        const i = this.insets.indexOf(inset);
        if (i >= 0) this.insets.splice(i, 1);
        this.appliers.delete(inset);
      },
    };
    this.insets.push(inset);
    this.appliers.set(inset, apply);
    return inset;
  }

  /** Re-derive every inset viewport (after engine.applyScale). */
  syncAll(): void {
    for (const i of this.insets) this.appliers.get(i)?.();
  }

  dispose(): void {
    for (const i of this.insets.slice()) i.dispose();
    if (this.obs) this.host.scene.onBeforeCameraRenderObservable.remove(this.obs);
    this.obs = null;
  }
}
